import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const EMPTY = { current_password: "", new_password: "", confirm_password: "" };

export default function ChangePasswordDialog({ open, onOpenChange, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", form);
      toast.success("Password changed. Please sign in again.");
      setForm(EMPTY);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(apiError(err, "Could not change password"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-navy">Change Password</DialogTitle>
          <DialogDescription>
            Enter your current password to set a new one. You will be signed out of all devices.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="cp-current">Current Password</Label>
            <Input
              id="cp-current"
              type="password"
              required
              autoComplete="current-password"
              data-testid="change-password-current"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="cp-new">New Password</Label>
            <Input
              id="cp-new"
              type="password"
              required
              autoComplete="new-password"
              data-testid="change-password-new"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="mt-1.5"
            />
            <p className="mt-1 text-xs text-muted-foreground">At least 8 characters.</p>
          </div>
          <div>
            <Label htmlFor="cp-confirm">Confirm New Password</Label>
            <Input
              id="cp-confirm"
              type="password"
              required
              autoComplete="new-password"
              data-testid="change-password-confirm"
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              className="mt-1.5"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="change-password-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-gold text-navy hover:bg-gold-dark hover:text-cream" data-testid="change-password-submit">
              {saving ? "Saving…" : "Change Password"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
